import { forwardRef, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Action, Tone, WithClassName } from './types';

/** Shared action treatment keeps async feedback errors visible and retryable. */
export function FeedbackAction({ action }: { action: Action }) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const pending = useRef(false);
  async function run() {
    if (pending.current) return;
    pending.current = true; setBusy(true); setFailed(false);
    try { await action.run(); }
    catch { setFailed(true); }
    finally { pending.current = false; setBusy(false); }
  }
  return <div className="ui-feedback__action">
    <button type="button" className={`ui-button ${action.tone === 'danger' ? 'ui-button--danger' : ''}`}
      disabled={busy || !!action.disabledReason} aria-describedby={action.disabledReason ? id : undefined}
      onClick={() => { void run(); }}>{busy ? 'Working…' : action.label}</button>
    {action.disabledReason && <span id={id}>{action.disabledReason}</span>}
    {failed && <p>Unable to complete this action. Please try again.</p>}
  </div>;
}

export type InlineAlertProps = WithClassName & {
  tone: Tone; title?: string; children?: ReactNode; action?: Action; actions?: ReactNode;
  /** Only urgent failures interrupt an assistive technology announcement. */
  urgent?: boolean;
};
export const InlineAlert = forwardRef<HTMLDivElement, InlineAlertProps>(function InlineAlert({
  tone, title, children, action, actions, urgent = false, className,
}, ref) {
  return <div ref={ref} className={`ui-feedback ui-tone-${tone} ${className ?? ''}`}
    role={urgent && tone === 'danger' ? 'alert' : 'status'}>
    {title && <p className="ui-feedback__title">{title}</p>}
    {children !== undefined && children !== null && <div>{children}</div>}
    {action && <FeedbackAction action={action} />}
    {actions}
  </div>;
});
